import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  ICreateFormPayload,
  IForm,
  IFormListItem,
  IUpdateFormPayload,
} from "@/types/(company)/forms.interface";

// RTK slice for the general forms module (`company/forms`). Backs the Studio
// Forms surface; the create-then-edit-by-id flow mirrors documents/workflows
// (create a draft, then autosave via `updateForm`, then `publishForm`).
export const companyFormsApi = createApi({
  reducerPath: "companyFormsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["Forms", "Form"],
  endpoints: (builder) => ({
    getForms: builder.query<
      IApiPaginatedResponse<IFormListItem[]>,
      IApiGeneralParams & { isActive?: boolean }
    >({
      query: (params) => ({ url: "/company/forms", method: "GET", params }),
      providesTags: ["Forms"],
    }),

    getFormById: builder.query<IApiResponse<IForm>, { formId: number | string }>(
      {
        query: ({ formId }) => ({
          url: `/company/forms/${formId}`,
          method: "GET",
        }),
        providesTags: (_r, _e, { formId }) => [{ type: "Form", id: formId }],
      },
    ),

    createForm: builder.mutation<IApiResponse<IForm>, ICreateFormPayload>({
      query: (body) => ({ url: "/company/forms", method: "POST", body }),
      invalidatesTags: ["Forms"],
    }),

    updateForm: builder.mutation<
      IApiResponse<IForm>,
      { formId: number | string; payload: IUpdateFormPayload }
    >({
      query: ({ formId, payload }) => ({
        url: `/company/forms/${formId}`,
        method: "PATCH",
        body: payload,
      }),
      // Autosave fires on a debounce throughout editing. Rather than invalidate
      // the open form's own tag (which refetches the whole form after every
      // save), patch the cached detail with the mutation's response so it stays
      // fresh with no extra round-trip. The list tag is still invalidated so
      // titles / status / field counts refresh on return to the listing.
      async onQueryStarted({ formId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            companyFormsApi.util.updateQueryData(
              "getFormById",
              { formId },
              (draft) => {
                Object.assign(draft, data);
              },
            ),
          );
        } catch {
          // Save failed — the builder surfaces its own error + retry affordance.
        }
      },
      invalidatesTags: ["Forms"],
    }),

    publishForm: builder.mutation<
      IApiResponse<IForm>,
      { formId: number | string }
    >({
      query: ({ formId }) => ({
        url: `/company/forms/${formId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { formId }) => [
        "Forms",
        { type: "Form", id: formId },
      ],
    }),

    setFormActive: builder.mutation<
      IApiResponse<IForm>,
      { formId: number | string; isActive: boolean }
    >({
      query: ({ formId, isActive }) => ({
        url: `/company/forms/${formId}/active`,
        method: "PATCH",
        body: { isActive },
      }),
      invalidatesTags: (_r, _e, { formId }) => [
        "Forms",
        { type: "Form", id: formId },
      ],
    }),

    cloneForm: builder.mutation<
      IApiResponse<IForm>,
      { formId: number | string }
    >({
      query: ({ formId }) => ({
        url: `/company/forms/${formId}/clone`,
        method: "POST",
      }),
      invalidatesTags: ["Forms"],
    }),

    deleteForm: builder.mutation<
      IApiResponse<null>,
      { formId: number | string }
    >({
      query: ({ formId }) => ({
        url: `/company/forms/${formId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Forms"],
    }),
  }),
});

export const {
  useGetFormsQuery,
  useGetFormByIdQuery,
  useLazyGetFormByIdQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  usePublishFormMutation,
  useSetFormActiveMutation,
  useCloneFormMutation,
  useDeleteFormMutation,
} = companyFormsApi;
